import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { SALES_DASHBOARD_URL } from '../shared/src';
import { ErrorHandlerService } from './error-handler.service';

export interface ShopProfilingPeriodFilter {
  month?: string;
  year?: string;
}

export interface ShopProfilingStoreFilter extends ShopProfilingPeriodFilter {
  page?: number;
  limit?: number;
  search?: string;
  region?: string;
  channel?: string;
  subChannel?: string;
  shopSize?: string;
  province?: string;
  signage?: string;
  turnoverMin?: number | string;
  turnoverMax?: number | string;
}

export interface ShopProfilingRawDataFilter extends ShopProfilingPeriodFilter {
  page?: number;
  limit?: number;
  search?: string;
  region?: string;
  province?: string;
  channel?: string;
  subChannel?: string;
  shopSize?: string;
  signage?: string;
  turnoverMin?: number | string;
  turnoverMax?: number | string;
}

@Injectable({
  providedIn: 'root',
})
export class ShopProfilingService {
  private readonly BASE = `${SALES_DASHBOARD_URL}/shop-profiling`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService,
  ) {}

  private buildParams(
    filters: object,
  ): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return params;
  }

  private unwrap = <T>(res: T | { result: T }): T =>
    (res as { result: T })?.result ?? (res as T);

  // ── Stores ──────────────────────────────────────────────────────────────────

  getStores(filters: ShopProfilingStoreFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/stores`, { params: this.buildParams(filters) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getStoreFilters(): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/stores/filters`)
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── Raw Data ─────────────────────────────────────────────────────────────────

  getRawData(filters: ShopProfilingRawDataFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/raw-data`, { params: this.buildParams(filters) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getRawDataColumns(): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/raw-data/columns`)
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getRawDataFilters(): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/raw-data/filters`)
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  exportRawData(filters: ShopProfilingRawDataFilter = {}): Observable<Blob> {
    return this.http
      .get(`${this.BASE}/raw-data/export`, {
        params: this.buildParams(filters),
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // ── Dashboard ────────────────────────────────────────────────────────────────

  getDashboardPeriods(): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/periods`)
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getDashboardOverview(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/overview`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getExecutiveSummary(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/executive-summary`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getPriorityTargetStores(filters: ShopProfilingPeriodFilter & { tier?: string } = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/priority-target-stores`, { params: this.buildParams(filters) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getHighValueStores(filters: ShopProfilingPeriodFilter & { region?: string } = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/high-value-stores`, { params: this.buildParams(filters) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getBrandPresence(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/brand-intel/presence`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getBrandPresenceStores(brand: string, period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/brand-intel/presence/stores`, {
        params: this.buildParams({ brand, ...period }),
      })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getBrandShare(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/brand-intel/share`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getBrandCount(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/brand-intel/count`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getBrandLargeStores(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/brand-intel/large`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getRegionalHeatmap(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/regional-heatmap`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getCoshelfCompetition(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/coshelf/competition`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getPositioningMatrix(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/coshelf/matrix`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getCoshelfInsights(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/coshelf/insights`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }

  getMapIntelligence(period: ShopProfilingPeriodFilter = {}): Observable<any> {
    return this.http
      .get<any>(`${this.BASE}/dashboard/map`, { params: this.buildParams(period) })
      .pipe(
        map(this.unwrap),
        catchError(this.errorHandler.handleError),
      );
  }
}
